import { Box, Button, DateInput, Modal, Select } from '@octano/global-ui';
import dayjs from 'dayjs';
import { useEffect, useMemo, useCallback } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'reactstrap';

import useServerSettings from '../../../hooks/useServerSettings';
import useTimeModules from '../../../hooks/useTimeModules';
import { useValidations } from '../../../hooks/useValidations';
import { TimeModule } from '../../../types/TimeModule';
import ClassroomLoader, { ClassroomLoaderFilters } from './ClassroomLoader';
import ClassroomSelector from './ClassroomSelector';

type TimeModuleOption = { label: string; value: string };

interface FormData {
  date: string;
  initModule: TimeModuleOption | null;
  endModule: TimeModuleOption | null;
  classroom?: { value: number; label: string; code?: string } | null;
}

export type SubmitData = Omit<FormData, 'initModule' | 'endModule'> & {
  courseName: string;
  initModule: TimeModule;
  endModule: TimeModule;
  classroomId?: number;
};

interface Props {
  courseName: string;
  lessonId: number;
  isOpen: boolean;
  currentClassroomCode: string;
  currentClassroomName: string;
  onConfirm?: (classData: SubmitData) => void;
  onCancel?: () => void;
  onDialogClose?: () => void;
}

const DEFAULT_VALUES: FormData = {
  date: '',
  initModule: null,
  endModule: null,
  classroom: null,
};

const parseTimeModule = (option: TimeModuleOption | null): TimeModule | null =>
  option ? JSON.parse(option.value) : null;

const isToday = (dateStr: string, offsetUTC: number) =>
  dayjs(dateStr).isSame(dayjs().utc().utcOffset(offsetUTC), 'day');

export default function ClassRecoveryModal({
  courseName,
  lessonId,
  isOpen,
  onConfirm = () => null,
  onCancel = () => null,
  onDialogClose = () => null,
  currentClassroomCode: currentClassroomId,
  currentClassroomName,
}: Props) {
  const { t } = useTranslation();
  const { timeModules } = useTimeModules();
  const { offset: offsetUTC } = useServerSettings();
  const { msgValidations } = useValidations();

  const methods = useForm<FormData>({ defaultValues: DEFAULT_VALUES });
  const { control, reset, setValue, getValues, watch, handleSubmit } = methods;

  const watchDate = watch('date');
  const watchInitModule = watch('initModule');
  const watchEndModule = watch('endModule');

  const filters = useMemo(() => {
    const getValue = (value?: string) =>
      value ? (JSON.parse(value).id as number) : undefined;
    return {
      date: watchDate,
      startModuleId: getValue(watchInitModule?.value),
      endModuleId: getValue(watchEndModule?.value),
      lessonId: lessonId,
    } as ClassroomLoaderFilters;
  }, [watchDate, watchInitModule, watchEndModule, lessonId]);

  useEffect(() => {
    if (isOpen) {
      reset(DEFAULT_VALUES);
    }
  }, [isOpen, reset]);
  const moduleOptions = useMemo(() => {
    const now = dayjs().utc().utcOffset(offsetUTC).second(0);

    const availableModules =
      watchDate && isToday(watchDate, offsetUTC)
        ? timeModules.filter((tm) => {
            const [h, m] = tm.endTime.split(':').map(Number);
            const moduleTime = dayjs()
              .utc()
              .utcOffset(offsetUTC)
              .hour(h)
              .minute(m)
              .second(0);
            return moduleTime.isAfter(now);
          })
        : timeModules;

    return availableModules.map((tm) => ({
      label: `${tm.shortening} - ${tm.name}`,
      value: JSON.stringify(tm),
    }));
  }, [watchDate, timeModules, offsetUTC]);

  const findModuleIndex = useCallback(
    (moduleId: number) =>
      moduleOptions.findIndex((mod) => JSON.parse(mod.value).id === moduleId),
    [moduleOptions],
  );

  const endModuleOptions = useMemo(() => {
    const init = parseTimeModule(watchInitModule);
    if (!init) return moduleOptions;

    const startIdx = findModuleIndex(init.id);
    return moduleOptions.slice(startIdx);
  }, [watchInitModule, moduleOptions, findModuleIndex]);

  const handleDialogClose = () => {
    reset();
    onDialogClose();
  };

  const handleFormSubmit = (formData: FormData) => {
    onConfirm({
      ...formData,
      courseName,
      initModule: parseTimeModule(formData.initModule)!,
      endModule: parseTimeModule(formData.endModule)!,
      classroomId: formData.classroom?.value,
    });
  };

  // Corrige módulo de fin si el inicio lo sobrepasa
  useEffect(() => {
    const start = parseTimeModule(watchInitModule);
    const end = parseTimeModule(getValues('endModule'));

    if (start && end) {
      const startIdx = findModuleIndex(start.id);
      const endIdx = findModuleIndex(end.id);
      if (startIdx > endIdx) setValue('endModule', watchInitModule);
    }
  }, [watchInitModule, findModuleIndex, getValues, setValue]);

  // Limpia módulos si no están disponibles en nueva fecha
  useEffect(() => {
    const { initModule } = getValues();
    if (
      initModule &&
      !moduleOptions.some((opt) => opt.value === initModule.value)
    ) {
      setValue('initModule', null);
      setValue('endModule', null);
    }
  }, [watchDate, moduleOptions, getValues, setValue]);

  return (
    <Modal
      size="md"
      isOpen={isOpen}
      toggle={() => null}
      onClosed={handleDialogClose}
    >
      <p className="text-center text-dark fw-700 fs-22 mb-5">
        {t(`classesHistory.recoverClass`)}
      </p>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Container>
            <Row className="pb-3">
              <Col lg="6" className="px-0 pr-lg-3 pb-3">
                <Box
                  color="primary"
                  variant="outlined"
                  title={t('common.terms.course')}
                  body={courseName}
                  style={{ padding: '8px 15px', minWidth: '100%' }}
                />
              </Col>
              <Col lg="6" className="px-0">
                <DateInput
                  minDate={new Date()}
                  label={t('common.terms.date')}
                  name="date"
                  control={control}
                  rules={{ required: msgValidations.required }}
                />
              </Col>
            </Row>
            <Row className="pb-3">
              <Col lg="6" className="px-0 pr-lg-3 pb-3">
                <Select
                  label={t('classesHistory.startModule')}
                  name="initModule"
                  options={moduleOptions}
                  control={control}
                  rules={{ required: msgValidations.required }}
                />
              </Col>
              <Col lg="6" className="px-0">
                <Select
                  label={t('classesHistory.endModule')}
                  name="endModule"
                  options={endModuleOptions}
                  control={control}
                  rules={{ required: msgValidations.required }}
                />
              </Col>
              <Col xs="12" className="px-0">
                <ClassroomLoader filters={filters}>
                  <ClassroomSelector
                    preselectId={currentClassroomId}
                    preselectName={currentClassroomName}
                  />
                </ClassroomLoader>
              </Col>
            </Row>
            <Row className="mt-4">
              <Col lg="6" className="px-0 pr-lg-3 mb-3 mb-lg-0">
                <Button
                  fullwidth
                  onClick={onCancel}
                  text={t('common.btnCancel')}
                  outlined
                />
              </Col>
              <Col lg="6" className="px-0">
                <Button fullwidth type="submit" text={t('common.btnConfirm')} />
              </Col>
            </Row>
          </Container>
        </form>
      </FormProvider>
    </Modal>
  );
}
